// Shared types and constants used across the webhook UI surfaces
// (list, view, edit, create dialog). Kept in plain `.ts` so it has no
// React deps and can be imported anywhere without bundling concerns.

export interface WebhookListRow {
	id: string
	label: string
	url: string
	events: readonly string[]
	active: boolean
	createdAt: number
}

// Resource-level subscription patterns surfaced in the picker.
// Mirrors `WEBHOOK_RESOURCES` in
// packages/api/src/lib/v2/webhooks/events.ts. Kept here (not
// imported) to avoid pulling server code into the bundle.
//
// We deliberately don't expose individual event types
// (e.g. `thread.created`) in the UI; splitting them per-action
// confused users into half-subscribing to a resource and missing
// related events. The granular event type still travels in the
// payload's `type` field so receivers can switch on the exact action.
export const WEBHOOK_RESOURCES = [
	{ id: "thread.*", label: "Threads" },
	{ id: "message.*", label: "Messages" },
	{ id: "comment.*", label: "Comments" },
	{ id: "contact.*", label: "Contacts" },
	{ id: "company.*", label: "Companies" },
	{ id: "changelog.*", label: "Changelogs" },
	{ id: "issue.*", label: "Issues" },
	{ id: "project.*", label: "Projects" },
	{ id: "customer_need.*", label: "Customer needs" },
	{ id: "doc.*", label: "Docs" },
	{ id: "tag.*", label: "Tags" },
	{ id: "membership.*", label: "Members" },
] as const

export type WebhookResourcePattern = (typeof WEBHOOK_RESOURCES)[number]["id"]

// Pre-resource webhooks may have stored granular event types
// (e.g. ["thread.created", "thread.updated"]). Collapse them into
// their resource-level wildcards for display + editing; server-side
// `subscriptionMatches` already handles both forms, so persisting the
// collapsed set is non-breaking.
export function collapseLegacyEvents(
	events: readonly string[],
): Set<WebhookResourcePattern> {
	const out = new Set<WebhookResourcePattern>()
	for (const e of events) {
		if (e === "*") {
			for (const r of WEBHOOK_RESOURCES) out.add(r.id)
			continue
		}
		if (e.endsWith(".*")) {
			const match = WEBHOOK_RESOURCES.find((r) => r.id === e)
			if (match) out.add(match.id)
			continue
		}
		// Granular form: derive resource prefix and match.
		const prefix = e.split(".")[0]
		const match = WEBHOOK_RESOURCES.find((r) => r.id === `${prefix}.*`)
		if (match) out.add(match.id)
	}
	return out
}

export const WEBHOOK_LIST_PATH = "/settings/integrations/api"

export function webhookViewPath(id: string): string {
	return `/settings/integrations/api/webhooks/${id}`
}

export function webhookEditPath(id: string): string {
	return `/settings/integrations/api/webhooks/${id}/edit`
}
